import React, { useEffect, useId, useRef } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, useTheme } from "@mui/material";
import { getContrastRatio } from "@mui/material/styles";
import { CheckCircleOutline, ErrorOutline, InfoOutlined, WarningAmber } from "@mui/icons-material";
import { tokens } from "../theme";

const TONE_ICONS = { warning: WarningAmber, error: ErrorOutline, info: InfoOutlined, success: CheckCircleOutline };

// Black or white, whichever keeps more contrast on both the button colour and its hover shade.
const confirmTextOf = (palette, tone) => {
    const { black, white } = palette.common;
    const worst = (text) => Math.min(getContrastRatio(text, palette[tone].main), getContrastRatio(text, palette[tone].dark));
    return worst(black) >= worst(white) ? black : white;
};

const focusRing = (t) => ({ "&.Mui-focusVisible": { outline: `2px solid ${t.palette.text.primary}`, outlineOffset: 2 } });

// The parent closes it (open = false) from onConfirm/onCancel; Esc and backdrop clicks call onCancel.
export default function ConfirmDialog({
    open = false,
    title,
    message,
    confirmLabel = "Continue",
    cancelLabel = "Go back",
    tone = "warning",
    onConfirm,
    onCancel,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const id = useId();
    const cancelAction = useRef(null);
    const settled = useRef(false);
    // The last open content, so the text stays on screen while the dialog fades out.
    const shown = useRef(null);
    if (open || !shown.current) shown.current = { title, message, confirmLabel, cancelLabel, tone };
    const view = shown.current;
    const toneKey = TONE_ICONS[view.tone] ? view.tone : "warning";
    const ToneIcon = TONE_ICONS[toneKey];

    useEffect(() => {
        if (open) settled.current = false;
    }, [open]);

    // One answer per opening, so a double click or a click during the fade can't fire twice.
    const settle = (callback) => {
        if (!open || settled.current) return;
        settled.current = true;
        if (callback) callback();
    };

    return (
        <Dialog
            open={open}
            onClose={() => settle(onCancel)}
            TransitionProps={{ onEntered: () => cancelAction.current?.focusVisible() }}
            maxWidth={false}
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-text`}
            PaperProps={{
                sx: {
                    width: "calc(100% - 32px)",
                    maxWidth: 420,
                    m: 2,
                    bgcolor: colors.primary[400],
                    backgroundImage: "none",
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                id={`${id}-title`}
                sx={{ display: "flex", alignItems: "center", gap: 1, typography: "h4", fontWeight: 600, pb: 1 }}
            >
                <ToneIcon color={toneKey} />
                {view.title}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={`${id}-text`} sx={{ lineHeight: 1.6 }}>
                    {view.message}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button action={cancelAction} variant="outlined" color="inherit" sx={focusRing} onClick={() => settle(onCancel)}>
                    {view.cancelLabel}
                </Button>
                <Button
                    variant="contained"
                    color={toneKey}
                    disableElevation
                    sx={(t) => ({ color: confirmTextOf(t.palette, toneKey), ...focusRing(t) })}
                    onClick={() => settle(onConfirm)}
                >
                    {view.confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
